import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  ChevronLeft,
  ChevronRight,
  ArrowRight,
  Heart,
  Star,
  Sparkles,
} from "lucide-react";
import { Button } from "./ui/button";

const HeroSection = () => {
  const { t, i18n } = useTranslation();
  const [currentSlide, setCurrentSlide] = useState(0);

  // Richtung -> Puffer neben Pfeilen
  const dir = i18n.dir();
  const sidePadForArrows =
    dir === "rtl" ? "pr-16 md:pr-28 lg:pr-36" : "pl-16 md:pl-28 lg:pl-36";

  // Deko-Linie (Position bleibt gleich)
  const decoLineSide = dir === "rtl" ? "md:right-0" : "md:left-0";

  const slides = [
    {
      title: t("hero.title2"),
      subtitle: t("hero.subtitle"),
      cta: t("hero.cta1"),
      image:
        "https://customer-assets.emergentagent.com/job_mindup-landing/artifacts/nm1er5gw_Gemini_Generated_Image_mmvpzsmmvpzsmmvp.png",
      overlay:
        "bg-gradient-to-r from-charcoal/70 via-warm-sage/50 to-transparent",
    },
    {
      title: t("hero.slide2_title"),
      subtitle: t("hero.slide2_subtitle"),
      cta: t("hero.slide2_cta"),
      image: "https://images.unsplash.com/photo-1701676639172-421b5e0b148b?q=80&w=1470&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D",
      overlay:
        "bg-gradient-to-r from-rich-chocolate/60 via-warm-sage/40 to-transparent",
    },
  ];

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 9000);
    return () => clearInterval(timer);
  }, [slides.length]);

  const nextSlide = () =>
    setCurrentSlide((prev) => (prev + 1) % slides.length);
  const prevSlide = () =>
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);

  // Ziel pro Slide: 0 -> #contact, 1 -> #domaines
  const handlePrimaryCta = (slideIndex) => {
    const id = slideIndex === 0 ? "contact" : "domaines";
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      id="hero"
      className="relative min-h-[100svh] md:min-h-screen flex items-center overflow-hidden"
    >
      {/* Icons Deko */}
      <div className="absolute inset-0 z-10 pointer-events-none">
        <div className="absolute top-20 left-10 opacity-20">
          <Heart className="h-16 w-16 text-warm-sage transform rotate-12 animate-pulse" />
        </div>
        <div className="absolute top-40 right-20 opacity-30">
          <Sparkles
            className="h-12 w-12 text-soft-taupe transform -rotate-12 animate-bounce"
            style={{ animationDelay: "1s" }}
          />
        </div>
        <div className="absolute bottom-40 left-20 opacity-25">
          <Star
            className="h-10 w-10 text-warm-sage transform rotate-45 animate-pulse"
            style={{ animationDelay: "2s" }}
          />
        </div>
      </div>

      {/* Linien Hintergrund */}
      <div className="absolute inset-0 z-5 pointer-events-none overflow-hidden">
        <svg
          className="absolute top-0 left-0 w-full h-full opacity-10"
          viewBox="0 0 1920 1080"
        >
          <path
            d="M-100,200 Q200,100 500,150 T900,200 Q1200,250 1500,180 T2000,250"
            stroke="url(#gradient1)"
            strokeWidth="2"
            fill="none"
            className="animate-pulse"
          />
          <path
            d="M-50,400 Q300,300 600,350 T1000,400 Q1300,450 1600,380 T2050,450"
            stroke="url(#gradient2)"
            strokeWidth="1.5"
            fill="none"
            className="animate-pulse"
            style={{ animationDelay: "1s" }}
          />
          <defs>
            <linearGradient id="gradient1" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="#C4B5A0" stopOpacity="0" />
              <stop offset="50%" stopColor="#C4B5A0" stopOpacity="1" />
              <stop offset="100%" stopColor="#C4B5A0" stopOpacity="0" />
            </linearGradient>
            <linearGradient id="gradient2" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="#D4C4B0" stopOpacity="0" />
              <stop offset="50%" stopColor="#D4C4B0" stopOpacity="1" />
              <stop offset="100%" stopColor="#D4C4B0" stopOpacity="0" />
            </linearGradient>
          </defs>
        </svg>
      </div>

      {/* Slides */}
      {slides.map((slide, index) => (
        <div
          key={index}
          className={`absolute inset-0 transition-all duration-6000 ${
            index === currentSlide
              ? "opacity-100 scale-100 pointer-events-auto z-20" /* ← FIX: klickbar */
              : "opacity-0 scale-105 pointer-events-none z-10" /* ← FIX: blockiert nicht */
          }`}
        >
          {/* Bild + Overlay */}
          <div className="absolute inset-0 overflow-hidden z-0">
            <img
              src={slide.image}
              alt="Mind'UP Hero"
              className={`
                w-full h-full object-cover transition-transform duration-[18000ms]
                object-[center_60%] md:object-[center_45%]
              `}
              style={{
                transform:
                  index === currentSlide ? "scale(1.08)" : "scale(1.0)",
              }}
            />
           <div className="absolute inset-0 md:hidden hero-contrast-m pointer-events-none " />
  <div className={`absolute inset-0 ${slide.overlay} hidden md:block pointer-events-none`} />          </div>

          {/* Inhalt */}
          <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-full flex items-center select-text">
            {/* ← FIX: select-text = Text markierbar */}
            <div className={`max-w-4xl ${sidePadForArrows}`}>
              {/* Tagline */}
              <div className="mb-6 md:mb-8">
                <div className="flex items-center mb-3 md:mb-4">
                  <div className="h-px bg-gradient-to-r from-warm-sage to-transparent w-16 md:w-20 mr-3 md:mr-4"></div>
                  <span className="text-cream-white/80 uppercase tracking-[0.25em] md:tracking-[0.3em] text-xs md:text-sm font-medium">
                    Mind'UP by Sanae Ait Balla
                  </span>
                </div>

                {/* Titel */}
                <h1
                  className="
                    text-[clamp(28px,7vw,56px)]
                    font-serif font-light text-cream-white
                    mb-5 md:mb-6
                    tracking-normal
                    leading-[1.3] md:leading-[1.2]
                    overflow-visible
                  "
                >
                  <span
                    className={`block transform transition-all duration-1000 ${
                      index === currentSlide
                        ? "translate-y-0 opacity-100"
                        : "translate-y-full opacity-0"
                    }`}
                  >
                    {slide.title}
                  </span>
                </h1>
              </div>

              {/* Untertitel + Linie */}
              <div className="relative mb-7 md:mb-8 overflow-visible">
                <div
                  className={`hidden md:block absolute top-0 bottom-0 w-1 rounded-full bg-gradient-to-b from-warm-sage to-soft-taupe ${decoLineSide}`}
                />
                <h2
                  className={`
                    text-[clamp(16px,4.5vw,22px)]
                    text-cream-white/90
                    leading-[1.8]
                    max-w-2xl
                    ${dir === "rtl" ? "md:pr-8" : "md:pl-8"}  /* dyn. Abstand zum Strich */
                    font-light
                    overflow-visible
                    transform transition-all duration-1200 delay-300
                    ${
                      index === currentSlide
                        ? "translate-y-0 opacity-100"
                        : "translate-y-8 opacity-0"
                    }
                  `}
                >
                  {slide.subtitle}
                </h2>
              </div>

              {/* CTA */}
              <div
                className={`
                  flex flex-col sm:flex-row gap-4 sm:gap-5
                  transform transition-all duration-1000 delay-600
                  ${
                    index === currentSlide
                      ? "translate-y-0 opacity-100"
                      : "translate-y-8 opacity-0"
                  }
                `}
              >
                <Button
                  size="lg"
                  aria-label="Start CTA"
                  onClick={() => handlePrimaryCta(index)} /* ← FIX: Ziel pro Slide */
                  className="group relative bg-warm-sage hover:bg-warm-sage/90 text-cream-white font-normal px-8 py-4 rounded-md transition-all duration-500 overflow-hidden shadow-lg hover:shadow-warm-sage/30 hover:scale-105 w-full sm:w-auto"
                >
                  <span className="relative z-10 flex items-center justify-center sm:justify-start text-sm md:text-base">
                    {slide.cta}
                    <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-2 transition-transform duration-300" />
                  </span>
                  <div className="absolute inset-0 bg-gradient-to-r from-warm-sage via-soft-taupe to-warm-sage transform -translate-x-full group-hover:translate-x-0 transition-transform duration-700"></div>
                </Button>
              </div>
            </div>
          </div>
        </div>
      ))}

      {/* Pfeile unten */}
      <div
        className="
          hero-nav
          absolute bottom-20 md:bottom-24
          inset-x-0 z-30
          flex justify-between
          px-6 md:px-10
          pointer-events-none
        "
      >
        <button
          onClick={prevSlide}
          aria-label={t("hero.prev")}
          className="
            p-4 rounded-full pointer-events-auto
            bg-cream-white/10 backdrop-blur-md text-cream-white
            hover:bg-cream-white/20 hover:scale-110
            transition-all duration-300 group
          "
        >
          <ChevronLeft
            size={28}
            className="group-hover:-translate-x-1 transition-transform"
          />
        </button>

        <button
          onClick={nextSlide}
          aria-label={t("hero.next")}
          className="
            p-4 rounded-full pointer-events-auto
            bg-cream-white/10 backdrop-blur-md text-cream-white
            hover:bg-cream-white/20 hover:scale-110
            transition-all duration-300 group
          "
        >
          <ChevronRight
            size={28}
            className="group-hover:translate-x-1 transition-transform"
          />
        </button>
      </div>

      {/* Punkte */}
      <div className="absolute bottom-12 left-1/2 -translate-x-1/2 z-30 flex space-x-4">
        {slides.map((_, i) => (
          <button
            key={i}
            onClick={() => setCurrentSlide(i)}
            className={`h-2 rounded-full transition-all duration-500 ${
              i === currentSlide
                ? "w-16 bg-warm-sage shadow-lg"
                : "w-8 bg-cream-white/40 hover:bg-cream-white/60"
            }`}
          />
        ))}
      </div>

      {/* Scroll-Indikator */}
      <div className="absolute bottom-8 right-8 z-30 flex flex-col items-center text-cream-white/60">
        <span className="text-xs uppercase tracking-wider mb-2 rotate-90 origin-center transform">
          Scroll
        </span>
        <div className="w-px h-12 bg-gradient-to-b from-cream-white/60 to-transparent animate-pulse"></div>
      </div>
    </section>
  );
};

export default HeroSection;
